<div class="dialog-header">
    <h2 mat-dialog-title>Manage Fund Report</h2>
    <span class="dialog-header-spacer"></span>
    <button mat-icon-button mat-dialog-close class="dialog-close" aria-label="Close" data-test="dialog-close">
        <mat-icon>close</mat-icon>
    </button>
</div>
<mat-dialog-content>
  @let isArchived = isArchived$ | async;
  <div class="report-actions-row">
    @if (isArchived?.value === false) {
      <button mat-stroked-button
              color="primary"
              data-test="add-report"
              title="Add Fund Report"
              aria-label="Add Fund Report"
              (click)="openAddReportDialog()">
        <mat-icon>add</mat-icon> Report
      </button>
    }
    <button mat-icon-button
            (click)="openTableSettings()"
            matTooltip="Table Settings"
            data-test="table-settings">
      <mat-icon>view_column</mat-icon>
    </button>
  </div>
  @if (fundReportDeleteError$ | async; as fundReportDeleteError) {
    @if (fundReportDeleteError.statusCode) {
      <valumize-info-panel
        [appearance]="'ERROR'"
        [message]="fundReportDeleteError.errorMessage">
      </valumize-info-panel>
    }
  }
  @let displayedColumns = (displayedColumns$ | async) ?? [];
  <table mat-table [dataSource]="dataSource" matSort #reportSort="matSort"
         [matSortDisableClear]="true" (matSortChange)="onSortChange($event)">
    <ng-container matColumnDef="selected">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let element">
        @if (element.id === currentFundReportId) {
          <mat-icon>check</mat-icon>
        }
      </td>
    </ng-container>
    <ng-container matColumnDef="id" sticky>
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Fund Report ID</th>
      <td mat-cell *matCellDef="let element">{{ element.id }}</td>
    </ng-container>
    <ng-container matColumnDef="reportDate" sticky>
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Report Date</th>
      <td mat-cell *matCellDef="let element">
        {{ element.reportDate | traceableFormat }}
      </td>
    </ng-container>
    <ng-container matColumnDef="unrealizedAssets">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Unreal. Assets</th>
      <td mat-cell *matCellDef="let element">{{ element.totalOfUnrealizedAssets | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="netCurrentAssets">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Net Curr. Assets</th>
      <td mat-cell *matCellDef="let element">{{ element.netCurrentAssets | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="gpCarryReserve">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>GP Carry Reserve</th>
      <td mat-cell *matCellDef="let element">{{ element.gpCarryReserve | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="calcNav">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Calc. NAV</th>
      <td mat-cell *matCellDef="let element">{{ element.totalNav | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="reportedNav">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Rep. NAV</th>
      <td mat-cell *matCellDef="let element">{{ element.totalReportedNav | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="totalDrawdown">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Total Drawdown</th>
      <td mat-cell *matCellDef="let element">{{ element.totalDrawDowns | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="reportedUndrawn">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Rep. Undrawn</th>
      <td mat-cell *matCellDef="let element">{{ element.reportedUndrawn | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="recallableDistr">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Recall. Distr.</th>
      <td mat-cell *matCellDef="let element">{{ element.recallableDistributions | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="totalUndrawn">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Total Undrawn</th>
      <td mat-cell *matCellDef="let element">{{ element.totalUndrawn | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="totalDistr">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Total Distr.</th>
      <td mat-cell *matCellDef="let element">{{ element.totalDistributions | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="currentTvpi">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Current TVPI</th>
      <td mat-cell *matCellDef="let element">{{ currentTvpi(element) | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="carryPaid">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Carry Paid</th>
      <td mat-cell *matCellDef="let element">{{ element.totalCarryPaid | traceableFormat }}</td>
    </ng-container>
    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let element">
        <button [matMenuTriggerFor]="menu" aria-label="More options" mat-icon-button (click)="$event.stopPropagation()">
          <mat-icon>more_vert</mat-icon>
        </button>
        <mat-menu #menu="matMenu">
          <button mat-menu-item [routerLink]="'../../funds/' + fundId"
            [queryParams]="{ fundReportId: element.id }"
            mat-dialog-close>
            <mat-icon>check</mat-icon>
            <span>Select</span>
          </button>
          @if (isArchived?.value === false) {
            <button
              mat-menu-item
              (click)="navigateToCopyWorkflowStepper(element)">
              <mat-icon>content_copy</mat-icon>
              Copy Fund Report
            </button>
          }
          @if (showDeleteButton) {
            <button mat-menu-item (click)="openDeleteConfirmation(element.id)">
              <mat-icon>delete</mat-icon>
              <span>Delete Fund Report</span>
            </button>
          }
        </mat-menu>
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns"
      [routerLink]="'../../funds/' + fundId" [queryParams]="{fundReportId: row.id}" mat-dialog-close
      (contextmenu)="onRowContextMenu($event, row)"></tr>
  </table>
  <valumize-context-menu #ctxMenu></valumize-context-menu>
</mat-dialog-content>
